import React from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import gql from 'graphql-tag';

import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';

import type {
	SegmentationTargetGroupsFieldGroupsQuery as SegmentationTargetGroupsFieldGroupsQuery$data,
	SegmentationTargetGroupsFieldGroupsQueryVariables as SegmentationTargetGroupsFieldGroupsQuery$variables,
} from './__types__/SegmentationTargetGroupsFieldGroupsQuery.ts';

type UseSegmentationTargetGroupsProps = {
	initialGroupIds: string[];
	nextCursor?: string;
};

type UseSegmentationTargetGroupsReturnType = {
	data?: SegmentationTargetGroupsFieldGroupsQuery$data;
	loading: boolean;
	errorComponent?: JSX.Element;
};

export const useSegmentationTargetGroups = ({
	nextCursor,
	initialGroupIds,
}: UseSegmentationTargetGroupsProps): UseSegmentationTargetGroupsReturnType => {
	const { data, loading, error } = useQuery<
		SegmentationTargetGroupsFieldGroupsQuery$data,
		SegmentationTargetGroupsFieldGroupsQuery$variables
	>(
		// eslint-disable-next-line graphql-relay-compat/consistent-graphql-operation-naming -- Experimental Schema GraphQL Operation
		gql`
			query SegmentationTargetGroupsFieldGroupsQuery($after: String, $first: Int = 25) {
				groups(after: $after, first: $first) {
					nodes {
						id
						name
					}
					pageInfo {
						endCursor
						hasNextPage
					}
				}
			}
		`,
		{
			variables: {
				after: nextCursor,
			},
			skip: initialGroupIds.length === 0,
		},
	);

	let errorComponent: undefined | JSX.Element = undefined;
	if (error && !loading) {
		if (isUnauthorizedError(error)) {
			markErrorAsHandled(error);
		} else {
			errorComponent = <ErrorDisplay error={error} />;
		}
	}

	return { data, loading, errorComponent };
};
